import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeHighlight from "rehype-highlight";
import { MermaidDiagram } from "./mermaid-diagram";
import type { Components } from "react-markdown";
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import {
  Info,
  Lightbulb,
  AlertCircle,
  AlertTriangle,
  OctagonAlert,
  ZoomIn,
  ZoomOut,
  X,
  Copy,
  Check,
  type LucideIcon,
} from "lucide-react";
import DOMPurify from "dompurify";
import { ContentOverlay } from "./content-overlay";
import {
  svgToPngBlob,
  copyImageToClipboard,
  imageToPngBlob,
  tableToTsv,
  copyTextToClipboard,
} from "../utils/copy-helpers";

/**
 * Recursively extract ALL text content from React children.
 * Handles strings, numbers, arrays, React elements, and iterables
 * (which react-markdown v10 / remark-breaks may produce).
 */
function getTextContent(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getTextContent).join("");

  // React element — recurse into its children
  if (typeof node === "object") {
    // Handle React elements with props.children
    if ("props" in node) {
      const props = node.props as Record<string, unknown>;
      // Skip <br> elements — treat as whitespace separator
      if ("type" in node && (node as unknown as { type: unknown }).type === "br") {
        return "\n";
      }
      return getTextContent(props?.children as ReactNode);
    }
    // Handle iterables (React 19 / react-markdown may yield these)
    if (Symbol.iterator in node) {
      const parts: string[] = [];
      for (const child of node as Iterable<ReactNode>) {
        parts.push(getTextContent(child));
      }
      return parts.join("");
    }
  }
  return "";
}

/**
 * Generate a URL-friendly slug from text.
 * Used to assign stable `id` attributes to rendered headings,
 * enabling scroll spy, getElementById-based scrollToHeading,
 * and future URL hash linking.
 */
function slugify(text: string): string {
  return text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
}

/**
 * Detect and extract a callout type from the React children tree.
 * Returns the matched callout type string (e.g. "NOTE") and the raw
 * prefix text that should be stripped, or null if no callout is found.
 *
 * This walks the tree to find the first meaningful text node and checks
 * if it starts with [!TYPE], which is more reliable than joining all
 * text and regex-matching — especially when remark-breaks inserts <br>
 * elements that fragment the text content.
 */
const CALLOUT_REGEX = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*/i;

function detectCalloutType(
  node: ReactNode,
): { type: string; prefix: string } | null {
  if (node == null || typeof node === "boolean") return null;
  if (typeof node === "string") {
    const m = node.match(CALLOUT_REGEX);
    if (m) return { type: m[1].toUpperCase(), prefix: m[0] };
    return null;
  }
  if (typeof node === "number") return null;

  if (Array.isArray(node)) {
    for (const child of node) {
      const result = detectCalloutType(child);
      if (result) return result;
      // If this child has non-empty text and didn't match, stop looking
      const text = getTextContent(child);
      if (text.trim().length > 0) return null;
    }
    return null;
  }

  if (typeof node === "object") {
    if ("props" in node) {
      const props = node.props as Record<string, unknown>;
      return detectCalloutType(props?.children as ReactNode);
    }
    if (Symbol.iterator in node) {
      for (const child of node as Iterable<ReactNode>) {
        const result = detectCalloutType(child);
        if (result) return result;
        const text = getTextContent(child);
        if (text.trim().length > 0) return null;
      }
    }
  }
  return null;
}

/**
 * Remove the [!TYPE] prefix text from React children, preserving structure.
 * Uses a mutable ref to track remaining prefix characters to strip,
 * which handles prefixes that span across multiple text nodes.
 */
function removeCalloutPrefix(children: ReactNode, prefix: string): ReactNode {
  let remaining = prefix;

  function strip(node: ReactNode): ReactNode {
    if (!remaining) return node;

    if (typeof node === "string") {
      if (remaining.length > 0) {
        if (node.startsWith(remaining)) {
          // This text node contains the rest of the prefix
          const result = node.slice(remaining.length);
          remaining = "";
          return result || null;
        }
        if (remaining.startsWith(node)) {
          // This entire text node is part of the prefix
          remaining = remaining.slice(node.length);
          return null;
        }
      }
      return node;
    }

    if (node == null || typeof node === "boolean" || typeof node === "number") {
      return node;
    }

    if (Array.isArray(node)) {
      return node.map(strip).filter((n) => n != null);
    }

    if (typeof node === "object" && "props" in node) {
      const props = node.props as Record<string, unknown>;
      const innerChildren = props?.children as ReactNode;
      const innerText = getTextContent(innerChildren);

      if (!remaining) return node;

      if (innerText && remaining.startsWith(innerText)) {
        // This element's entire text is part of the prefix
        remaining = remaining.slice(innerText.length);
        return null;
      }
      if (innerText && innerText.startsWith(remaining)) {
        // The remaining prefix is within this element
        const newChildren = strip(innerChildren);
        return React.cloneElement(
          node as React.ReactElement,
          {},
          newChildren,
        );
      }
      return node;
    }

    return node;
  }

  return React.Children.map(children, (child) => strip(child));
}

// Callout type mapping
const CALLOUT_TYPES: Record<
  string,
  { label: string; className: string; Icon: LucideIcon }
> = {
  NOTE: {
    label: "Note",
    className: "callout-note",
    Icon: Info,
  },
  TIP: {
    label: "Tip",
    className: "callout-tip",
    Icon: Lightbulb,
  },
  IMPORTANT: {
    label: "Important",
    className: "callout-important",
    Icon: AlertCircle,
  },
  WARNING: {
    label: "Warning",
    className: "callout-warning",
    Icon: AlertTriangle,
  },
  CAUTION: {
    label: "Caution",
    className: "callout-caution",
    Icon: OctagonAlert,
  },
};

// Video URL detection helpers
function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function getVimeoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
}

function isDirectVideo(url: string): boolean {
  const isVideoFile = /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
  return isVideoFile;
}

/**
 * Parse optional dimensions from the alt text.
 * Supports: "alt|400" (width only) or "alt|400x300" (width x height).
 * Returns the clean alt text and optional width/height.
 */
function parseDimensions(alt: string | undefined): {
  cleanAlt: string;
  width?: number;
  height?: number;
} {
  if (!alt) return { cleanAlt: "" };
  const match = alt.match(/^(.+?)\|(\d+)(?:x(\d+))?$/);
  if (!match) return { cleanAlt: alt };
  return {
    cleanAlt: match[1].trim(),
    width: parseInt(match[2], 10),
    height: match[3] ? parseInt(match[3], 10) : undefined,
  };
}

// ── Mermaid Expandable Wrapper ──

/**
 * Extract the SVG from the rendered mermaid container, deduplicate its ID
 * (to avoid style conflicts with the original), and prepare it for
 * scale-to-fit display in the lightbox.
 */
function extractMermaidSvg(container: HTMLElement): string | null {
  const svgEl = container.querySelector("svg");
  if (!svgEl) return null;

  const clone = svgEl.cloneNode(true) as SVGElement;

  // Rename the clone's ID so its internal <style> selectors don't
  // collide with the still-mounted original SVG
  const oldId = clone.getAttribute("id") || "";
  const newId = `lb_${Date.now()}`;
  clone.setAttribute("id", newId);
  if (oldId) {
    clone.querySelectorAll("style").forEach((s) => {
      if (s.textContent) {
        s.textContent = s.textContent.replaceAll(`#${oldId}`, `#${newId}`);
      }
    });
  }

  // Ensure viewBox exists for proper aspect-ratio scaling
  if (!clone.getAttribute("viewBox")) {
    const rect = svgEl.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      clone.setAttribute("viewBox", `0 0 ${Math.round(rect.width)} ${Math.round(rect.height)}`);
    }
  }

  // Set dimensions to 100% so the SVG fills its container.
  clone.setAttribute("width", "100%");
  clone.setAttribute("height", "100%");
  clone.style.cssText = "";

  return clone.outerHTML;
}



// ── Code Block Copy Button ──

function CodeBlockCopyButton({ preRef }: { preRef: React.RefObject<HTMLPreElement | null> }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!preRef.current) return;
    const code = preRef.current.querySelector("code");
    const text = code?.textContent ?? preRef.current.textContent ?? "";
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("mojoReader: Failed to copy code block text", err);
    }
  }, [preRef]);

  return (
    <button
      className="code-block-copy-btn"
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy code"}
      aria-label={copied ? "Copied to clipboard" : "Copy code to clipboard"}
    >
      {copied ? <Check className="code-block-copy-icon" /> : <Copy className="code-block-copy-icon" />}
    </button>
  );
}


function MermaidExpandable({
  chart,
  onExpand,
}: {
  chart: string;
  onExpand: (svgHtml: string, initialZoom: number) => void;
}) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const handleExpand = React.useCallback(() => {
    if (!wrapperRef.current) return;
    const zoom = 1;
    const svgHtml = extractMermaidSvg(wrapperRef.current);
    if (svgHtml) onExpand(svgHtml, zoom);
  }, [onExpand]);

  const handleCopy = React.useCallback(async () => {
    if (!wrapperRef.current) return;
    const svg = wrapperRef.current.querySelector("svg");
    if (!svg) return;
    try {
      const blob = await svgToPngBlob(svg);
      await copyImageToClipboard(blob);
    } catch {
      // Fallback: copy raw SVG markup as text
      const svgString = new XMLSerializer().serializeToString(svg);
      try {
        await copyTextToClipboard(svgString);
      } catch {
        // Both clipboard methods failed (e.g. non-secure context)
      }
    }
  }, []);

  return (
    <ContentOverlay
      type="diagram"
      onCopy={handleCopy}
      onExpand={handleExpand}
    >
      <div
        ref={wrapperRef}
        className="md-expandable"
        role="button"
        tabIndex={0}
        title="Click to expand diagram"
        onClick={handleExpand}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleExpand();
          }
        }}
      >
        <MermaidDiagram chart={chart} />
      </div>
    </ContentOverlay>
  );
}

// ── Lightbox state type ──

interface LightboxState {
  /** Image src — renders an <img> */
  src?: string;
  /** Raw HTML content — renders via dangerouslySetInnerHTML */
  htmlContent?: string;
  /** Label shown in the top bar */
  label: string;
  /** Use scale-to-fit rendering (for diagrams) */
  isDiagram?: boolean;
  /** Initial zoom level (calculated from text readability) */
  initialZoom?: number;
}

// ── Zoom Percentage Input (click to edit zoom level) ──

interface ZoomPercentageInputProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
}

function ZoomPercentageInput({ zoom, onZoomChange }: ZoomPercentageInputProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setInputValue(String(Math.round(zoom * 100)));
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commitValue = () => {
    const parsed = parseInt(inputValue, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.min(Math.max(parsed, 10), 500);
      onZoomChange(clamped / 100);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      commitValue();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <span className="lightbox-zoom-level" style={{ display: 'inline-flex', alignItems: 'center', gap: '1px' }}>
        <input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={commitValue}
          onKeyDown={handleKeyDown}
          className="lightbox-zoom-input"
        />
        <span style={{ fontSize: '10px', fontWeight: 900, color: 'rgba(255,255,255,0.6)' }}>%</span>
      </span>
    );
  }

  return (
    <span
      className="lightbox-zoom-level"
      onClick={startEditing}
      style={{ cursor: "pointer" }}
      title="Click to edit zoom level"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEditing(e);
        }
      }}
    >
      {Math.round(zoom * 100)}%
    </span>
  );
}

// ── Content Lightbox (images, diagrams, tables, math) ──
// Merged interaction model: scroll-wheel zoom (ADR-003) + mojoDoc's drag pan,
// pinch-to-zoom, editable zoom %, transform origin tracking, and close guard.

function ContentLightbox({
  state,
  onClose,
}: {
  state: LightboxState;
  onClose: () => void;
}) {
  const baseZoom = state.initialZoom ?? 1;
  const [zoom, setZoom] = useState(baseZoom);

  const [transformOriginX, setTransformOriginX] = useState<string | number>("center");
  const [transformOriginY, setTransformOriginY] = useState<string | number>("center");
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const dragStartRef = useRef({ x: 0, y: 0 });
  const translateStartRef = useRef({ x: 0, y: 0 });
  const didDragRef = useRef(false);
  const closingRef = useRef(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const touchStateRef = useRef<{
    startX: number;
    startY: number;
    startTranslateX: number;
    startTranslateY: number;
    startDist: number;
    startZoom: number;
    isPinching: boolean;
    moved: boolean;
  } | null>(null);

  // Zoom functions — position-aware
  const zoomInTowards = useCallback((x: string | number, y: string | number, amount: number = 0.25) => {
    setTransformOriginX(x);
    setTransformOriginY(y);
    setZoom((z) => Math.min(z + amount, 5));
  }, []);

  const zoomOutTowards = useCallback((_x: string | number, _y: string | number, amount: number = 0.25) => {
    setZoom((z) => {
      const next = Math.max(z - amount, 0.1);
      if (next <= 1) {
        setTransformOriginX("center");
        setTransformOriginY("center");
        setTranslateX(0);
        setTranslateY(0);
      }
      return next;
    });
  }, []);

  const handleZoomIn = useCallback(() => {
    zoomInTowards("center", "center", 0.25);
  }, [zoomInTowards]);

  const handleZoomOut = useCallback(() => {
    zoomOutTowards("center", "center", 0.25);
  }, [zoomOutTowards]);

  const handleResetZoom = useCallback(() => {
    setZoom(baseZoom);
    setTransformOriginX("center");
    setTransformOriginY("center");
    setTranslateX(0);
    setTranslateY(0);
  }, [baseZoom]);

  // Keep a stable ref to onClose to avoid dependency chain churn
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  // Guarded close — prevents double-fire from rapid clicks
  const handleClose = useCallback((e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.stopPropagation();
      if ("detail" in e && e.detail > 1) {
        return;
      }
    }
    if (closingRef.current) return;
    closingRef.current = true;
    onCloseRef.current();
  }, []);

  // Zoom change handler for ZoomPercentageInput — resets origin/translate when zoom ≤ 1
  const handleZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom);
    if (newZoom <= 1) {
      setTransformOriginX("center");
      setTransformOriginY("center");
      setTranslateX(0);
      setTranslateY(0);
    }
  }, []);

  // Focus trap + Scroll Preservation
  useEffect(() => {
    triggerRef.current = document.activeElement;

    const savedScrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;

    // Lock body scroll — prevents iOS/Android page scroll behind the fixed lightbox overlay
    const savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const timer = requestAnimationFrame(() => {
      if (overlayRef.current) {
        const focusable = overlayRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length > 0) {
          focusable[focusable.length - 1].focus();
        }
      }
    });

    const handleTabTrap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !overlayRef.current) return;
      const focusable = overlayRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', handleTabTrap);

    return () => {
      cancelAnimationFrame(timer);
      window.removeEventListener('keydown', handleTabTrap);
      document.body.style.overflow = savedOverflow;
      if (triggerRef.current && triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus({ preventScroll: true });
      }
      window.scrollTo(0, savedScrollTop);
    };
  }, []);

  // Keyboard shortcuts (capture phase to intercept document shortcuts)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleClose();
      }
      if (e.key === "+" || e.key === "=") {
        e.stopPropagation();
        handleZoomIn();
      }
      if (e.key === "-") {
        e.stopPropagation();
        handleZoomOut();
      }
      if (e.key === "0") {
        e.stopPropagation();
        handleResetZoom();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [handleClose, handleZoomIn, handleZoomOut, handleResetZoom]);

  // Mouse Drag / Panning (only when zoomed > 1)
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom <= 1) return;
    if (e.button !== 0) return; // Left click only
    e.preventDefault();
    setIsDragging(true);
    didDragRef.current = false;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    translateStartRef.current = { x: translateX, y: translateY };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        didDragRef.current = true;
      }
      setTranslateX(translateStartRef.current.x + deltaX);
      setTranslateY(translateStartRef.current.y + deltaY);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // Scroll-wheel zoom (ADR-003: Google Maps / Figma interaction model)
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Zoom towards cursor position
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setTransformOriginX(x);
      setTransformOriginY(y);
    }

    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setZoom(z => {
      const next = Math.min(Math.max(z + delta, 0.1), 5);
      if (next <= 1) {
        setTransformOriginX("center");
        setTransformOriginY("center");
        setTranslateX(0);
        setTranslateY(0);
      }
      return next;
    });
  }, []);

  // Attach wheel handler via ref (passive: false required for preventDefault)
  const contentInnerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = contentInnerRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Stable refs for touch handlers — avoids re-registering listeners on every state change
  const zoomRef = useRef(zoom);
  const translateXRef = useRef(translateX);
  const translateYRef = useRef(translateY);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { translateXRef.current = translateX; }, [translateX]);
  useEffect(() => { translateYRef.current = translateY; }, [translateY]);

  // Mobile Touch Gestures (Pinch to zoom + drag pan)
  // Native listeners with { passive: false } so preventDefault() works.
  // Registered ONCE — reads current state from refs, not closures.
  useEffect(() => {
    const el = contentInnerRef.current;
    if (!el) return;

    const handleTouchStart = (e: TouchEvent) => {
      const currentZoom = zoomRef.current;
      // At zoom=1 with scrollable HTML content (tables), let the browser
      // handle touch natively so the user can scroll horizontally.
      const allowNativeScroll = currentZoom <= 1 && state.htmlContent && !state.isDiagram;
      if (allowNativeScroll) return;

      e.stopPropagation();

      if (e.touches.length === 1) {
        // No setState here — React re-renders during touch cancel the gesture
        e.preventDefault();
        const touch = e.touches[0];
        touchStateRef.current = {
          startX: touch.clientX,
          startY: touch.clientY,
          startTranslateX: translateXRef.current,
          startTranslateY: translateYRef.current,
          startDist: 0,
          startZoom: currentZoom,
          isPinching: false,
          moved: false,
        };
      } else if (e.touches.length === 2) {
        e.preventDefault();
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const midX = (t1.clientX + t2.clientX) / 2;
        const midY = (t1.clientY + t2.clientY) / 2;

        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const x = ((midX - rect.left) / rect.width) * 100;
          const y = ((midY - rect.top) / rect.height) * 100;
          setTransformOriginX(x);
          setTransformOriginY(y);
        }

        touchStateRef.current = {
          startX: midX,
          startY: midY,
          startTranslateX: translateXRef.current,
          startTranslateY: translateYRef.current,
          startDist: dist,
          startZoom: currentZoom,
          isPinching: true,
          moved: false,
        };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStateRef.current) return;
      e.stopPropagation();
      e.preventDefault();

      const touchState = touchStateRef.current;
      const currentZoom = zoomRef.current;

      if (e.touches.length === 1 && !touchState.isPinching) {
        const touch = e.touches[0];
        const deltaX = touch.clientX - touchState.startX;
        const deltaY = touch.clientY - touchState.startY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          touchState.moved = true;
        }
        // Only apply translation when zoomed in — match mouse drag guard (L721)
        if (currentZoom > 1) {
          setTranslateX(touchState.startTranslateX + deltaX);
          setTranslateY(touchState.startTranslateY + deltaY);
        }
      } else if (e.touches.length === 2 && touchState.isPinching) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const midX = (t1.clientX + t2.clientX) / 2;
        const midY = (t1.clientY + t2.clientY) / 2;

        if (touchState.startDist > 0) {
          const factor = dist / touchState.startDist;
          const nextZoom = Math.min(Math.max(touchState.startZoom * factor, 0.1), 5);
          if (nextZoom <= 1) {
            setTransformOriginX("center");
            setTransformOriginY("center");
            setTranslateX(0);
            setTranslateY(0);
          }
          setZoom(nextZoom);
        }

        if (currentZoom > 1) {
          const deltaX = midX - touchState.startX;
          const deltaY = midY - touchState.startY;
          if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
            touchState.moved = true;
          }
          setTranslateX(touchState.startTranslateX + deltaX);
          setTranslateY(touchState.startTranslateY + deltaY);
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Only intercept if we were tracking a custom gesture
      if (touchStateRef.current) {
        e.stopPropagation();
        if (touchStateRef.current.moved) {
          didDragRef.current = true;
        }
        touchStateRef.current = null;
      }
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };

  }, [state.htmlContent, state.isDiagram]);

  // Scroll indicator measurements — computed in useLayoutEffect to avoid
  // reading refs during render (React 19 concurrent mode violation).
  // useLayoutEffect fires synchronously before paint, so indicators
  // appear in the correct position without flicker.
  //
  // eslint-disable: set-state-in-effect is intentional — this is the textbook
  // useLayoutEffect pattern: measure DOM → setState before paint. The
  // "cascading render" is desired (synchronous, pre-paint update).
  const [scrollIndicatorData, setScrollIndicatorData] = useState<{
    showH: boolean;
    showV: boolean;
    hThumbW: number;
    hPos: number;
    vThumbH: number;
    vPos: number;
  } | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect -- textbook useLayoutEffect: measure DOM → setState before paint */
  useLayoutEffect(() => {
    if (zoom <= 1) {
      setScrollIndicatorData(null);
      return;
    }
    const area = contentAreaRef.current;
    const inner = contentInnerRef.current;
    if (!area || !inner) {
      setScrollIndicatorData(null);
      return;
    }

    const areaRect = area.getBoundingClientRect();
    const innerRect = inner.getBoundingClientRect();

    const scaledW = innerRect.width;
    const scaledH = innerRect.height;

    const showH = scaledW > areaRect.width;
    const showV = scaledH > areaRect.height;

    if (!showH && !showV) {
      setScrollIndicatorData(null);
      return;
    }

    const hThumbW = showH ? Math.max(20, (areaRect.width / scaledW) * 100) : 0;
    const maxPanX = (scaledW - areaRect.width) / 2;
    const hPos = showH && maxPanX > 0 ? ((maxPanX - translateX) / (2 * maxPanX)) * (100 - hThumbW) : 0;

    const vThumbH = showV ? Math.max(20, (areaRect.height / scaledH) * 100) : 0;
    const maxPanY = (scaledH - areaRect.height) / 2;
    const vPos = showV && maxPanY > 0 ? ((maxPanY - translateY) / (2 * maxPanY)) * (100 - vThumbH) : 0;

    setScrollIndicatorData({ showH, showV, hThumbW, hPos, vThumbH, vPos });
  }, [zoom, translateX, translateY]);
  /* eslint-enable react-hooks/set-state-in-effect */

  return (
    <div className="lightbox-overlay" ref={overlayRef} role="dialog" aria-modal="true" aria-label={state.label || "Content viewer"}>
      {/* Backdrop */}
      <div className="lightbox-backdrop" onClick={handleClose} />

      {/* Modal Content */}
      <div className="lightbox-modal">
        {/* Top Bar */}
        <div className="lightbox-topbar">
          <div className="lightbox-topbar-label">
            {state.label && (
              <h2>{state.label}</h2>
            )}
          </div>

          <div className="lightbox-topbar-actions">
            <div className="lightbox-zoom-controls">
              <button
                onClick={handleZoomOut}
                className="lightbox-btn"
                title="Zoom Out"
                aria-label="Zoom out"
              >
                <ZoomOut className="lightbox-icon" />
              </button>
              <ZoomPercentageInput zoom={zoom} onZoomChange={handleZoomChange} />
              <button
                onClick={handleZoomIn}
                className="lightbox-btn"
                title="Zoom In"
                aria-label="Zoom in"
              >
                <ZoomIn className="lightbox-icon" />
              </button>
              <button
                onClick={handleResetZoom}
                className="lightbox-btn lightbox-btn-text"
              >
                Reset
              </button>
            </div>

            <button
              onClick={handleClose}
              onDoubleClick={(e) => e.stopPropagation()}
              className="lightbox-btn lightbox-close-btn"
              title="Close"
              aria-label="Close lightbox"
            >
              <X style={{ width: 20, height: 20 }} />
            </button>
          </div>
        </div>

        {/* Content Area — scroll-wheel zoom, click-drag pan */}
        <div ref={contentAreaRef} className="lightbox-content-area" onClick={handleClose} style={{
          touchAction: zoom <= 1 && state.htmlContent && !state.isDiagram ? "auto" : "none",
          overflow: zoom <= 1 && state.htmlContent && !state.isDiagram ? "auto" : "hidden",
        }}>
          <div
            ref={contentInnerRef}
            className={`lightbox-content-inner${zoom <= 1 && state.htmlContent && !state.isDiagram ? " lightbox-content-inner--scrollable" : ""}`}
            style={{
              transform: `translate(${translateX}px, ${translateY}px) scale(${zoom})`,
              transformOrigin: typeof transformOriginX === "number" ? `${transformOriginX}% ${transformOriginY}%` : `${transformOriginX} ${transformOriginY}`,
              cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              touchAction: zoom <= 1 && state.htmlContent && !state.isDiagram ? "auto" : "none",
            }}
            onMouseDown={handleMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              if (didDragRef.current) {
                didDragRef.current = false;
                return;
              }
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleResetZoom();
            }}
          >
            {/* Touch-capture overlay for diagrams (ADR-006)
                Chrome mobile doesn't reliably honor CSS touch-action: none on SVG
                namespace elements. This transparent HTML div sits on top of the mermaid
                SVG so all touches land on a standard element where touch-action works.
                Events bubble up to contentInnerRef where existing handlers process them. */}
            {state.isDiagram && (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  touchAction: 'none',
                }}
              />
            )}
            {state.src ? (
              <img
                src={state.src}
                alt={state.label}
                className="lightbox-image"
                draggable={false}
              />
            ) : state.htmlContent ? (
              <div
                className={`${state.isDiagram ? "lightbox-mermaid-container" : "lightbox-html-content"} lightbox-html-panel`}
                // Mermaid diagrams: skip DOMPurify — it strips <foreignObject> elements
                // that mermaid uses for text labels. The SVG is already trusted (cloned
                // from a MermaidDiagram rendered with securityLevel: "strict").
                // Non-diagram HTML (tables, etc.) is still sanitized.
                dangerouslySetInnerHTML={{ __html: state.isDiagram ? state.htmlContent : DOMPurify.sanitize(state.htmlContent, { USE_PROFILES: { svg: true, html: true } }) }}
              />
            ) : null}
          </div>
        </div>

        {/* Scroll position indicators — rendered at modal level to sit ABOVE the transformed content */}
        {scrollIndicatorData && (
          <>
            {scrollIndicatorData.showH && (
              <div className="lightbox-scrollbar lightbox-scrollbar-h">
                <div
                  className="lightbox-scrollbar-thumb"
                  style={{ width: `${scrollIndicatorData.hThumbW}%`, left: `${Math.max(0, Math.min(100 - scrollIndicatorData.hThumbW, scrollIndicatorData.hPos))}%` }}
                />
              </div>
            )}
            {scrollIndicatorData.showV && (
              <div className="lightbox-scrollbar lightbox-scrollbar-v">
                <div
                  className="lightbox-scrollbar-thumb"
                  style={{ height: `${scrollIndicatorData.vThumbH}%`, top: `${Math.max(0, Math.min(100 - scrollIndicatorData.vThumbH, scrollIndicatorData.vPos))}%` }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

function TableExpandable({
  children,
  onExpand,
  ...props
}: React.ComponentPropsWithoutRef<'table'> & {
  onExpand: (html: string) => void;
}) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const pointerStartRef = React.useRef<{ x: number; y: number } | null>(null);

  const handleTableExpand = () => {
    const html = wrapperRef.current?.innerHTML;
    if (html) onExpand(html);
  };

  const handleTableCopy = async () => {
    const tableEl = wrapperRef.current?.querySelector("table");
    if (!tableEl) return;
    const tsv = tableToTsv(tableEl);
    try {
      await copyTextToClipboard(tsv);
    } catch {
      // Clipboard API unavailable (e.g. non-secure context)
    }
  };

  // Detect overflow + track scroll position for mobile affordance
  React.useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const checkOverflow = () => {
      const isOverflowing = wrapper.scrollWidth > wrapper.clientWidth + 2;
      wrapper.classList.toggle("table-scrollable", isOverflowing);
      if (isOverflowing) {
        // Check if already scrolled to end
        const atEnd =
          wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 2;
        wrapper.classList.toggle("scrolled-end", atEnd);
      }
    };

    const handleScroll = () => {
      const atEnd =
        wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 2;
      wrapper.classList.toggle("scrolled-end", atEnd);
    };

    // Check on mount (after table renders)
    const raf = requestAnimationFrame(checkOverflow);

    // Re-check on resize
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(wrapper);

    wrapper.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      wrapper.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <ContentOverlay
      type="table"
      onCopy={handleTableCopy}
      onExpand={handleTableExpand}
    >
      <div
        ref={wrapperRef}
        className="table-wrapper md-expandable"
        role="button"
        tabIndex={0}
        title="Click to expand"
        onPointerDown={(e) => {
          // Track start position to distinguish tap from swipe
          pointerStartRef.current = { x: e.clientX, y: e.clientY };
        }}
        onClick={(e) => {
          const start = pointerStartRef.current;
          if (start) {
            const dx = Math.abs(e.clientX - start.x);
            const dy = Math.abs(e.clientY - start.y);
            // If user swiped > 5px, it's a scroll gesture, not a tap
            if (dx > 5 || dy > 5) return;
          }
          handleTableExpand();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleTableExpand();
          }
        }}
      >
        <table {...props}>{children}</table>
      </div>
    </ContentOverlay>
  );
}

export function MarkdownRenderer({
  content,
  className,
}: MarkdownRendererProps) {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  // Use a ref so the components object never depends on a changing closure.
  // This prevents ReactMarkdown from re-rendering (and re-mounting every
  // MermaidDiagram) when lightbox state changes.
  const setLightboxRef = useRef(setLightbox);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally no deps: "latest callback" pattern, must update ref after every render
  useEffect(() => {
    setLightboxRef.current = setLightbox;
  });

  const components: Components = useMemo(() => ({
    // Override code blocks to handle mermaid
    code({ className: codeClassName, children, ...props }) {
      const match = /language-(\w+)/.exec(codeClassName || "");
      const lang = match?.[1];

      // Handle mermaid diagrams
      if (lang === "mermaid") {
        const chartText = String(children).replace(/\n$/, "");
        return (
          <MermaidExpandable
            chart={chartText}
            onExpand={(svgHtml, initialZoom) =>
              setLightboxRef.current({ htmlContent: svgHtml, label: "Diagram", isDiagram: true, initialZoom })
            }
          />
        );
      }

      // Determine if this is inline code vs block code.
      // Block code lives inside a <pre> parent (node.position is set by
      // the AST and the pre override wraps it in .code-block).
      // In react-markdown, block code elements have a parent `pre` in the
      // HAST tree. We can detect "inline" by checking whether the hast
      // node's parent is NOT a <pre> — but the simplest heuristic is:
      // if there's no className AND the content contains newlines, it's
      // a block. Otherwise if no className and no newlines, it's inline.
      const text = String(children);
      const isBlock = codeClassName || text.includes("\n");

      if (!isBlock) {
        return (
          <code className="inline-code" {...props}>
            {children}
          </code>
        );
      }

      // For highlighted code blocks, rehype-highlight handles the rest
      return (
        <code className={codeClassName} {...props}>
          {children}
        </code>
      );
    },

    // Override pre blocks for code containers
    pre({ children, ...props }) {
      // Check if the child is a mermaid diagram (already rendered by code override).
      // We need multiple detection strategies because react-markdown v10 / React 19
      // may pass children in different shapes (single element, array, fragment).
      function isMermaidElement(el: unknown): boolean {
        if (!el || typeof el !== "object") return false;
        const element = el as Record<string, unknown>;
        // Direct type match
        if ("type" in element) {
          const t = element.type;
          if (t === MermaidDiagram || t === MermaidExpandable) return true;
          if (
            t &&
            typeof t === "function" &&
            ((t as { displayName?: string }).displayName === "MermaidDiagram" ||
              (t as { displayName?: string }).displayName === "MermaidExpandable")
          )
            return true;
        }
        // Props-based detection (chart prop is unique to mermaid components)
        if (
          "props" in element &&
          element.props &&
          typeof element.props === "object" &&
          "chart" in (element.props as Record<string, unknown>)
        )
          return true;
        return false;
      }

      // Single element child
      if (isMermaidElement(children)) {
        return <>{children}</>;
      }

      // Array children (react-markdown v10 may wrap in array)
      if (Array.isArray(children) && children.some(isMermaidElement)) {
        return <>{children}</>;
      }

      const preRef = React.createRef<HTMLPreElement>();
      return (
        <pre className="code-block" ref={preRef} {...props}>
          {children}
          <CodeBlockCopyButton preRef={preRef} />
        </pre>
      );
    },

    // Override blockquote for callout support
    blockquote({ children, ...props }) {
      const callout = detectCalloutType(children);

      if (callout) {
        const config = CALLOUT_TYPES[callout.type];
        if (config) {
          const cleaned = removeCalloutPrefix(children, callout.prefix);
          return (
            <div className={`callout ${config.className}`}>
              <div className="callout-title">
                <config.Icon className="callout-icon" size={16} />
                <span>{config.label}</span>
              </div>
              <div className="callout-content">{cleaned}</div>
            </div>
          );
        }
      }

      return <blockquote {...props}>{children}</blockquote>;
    },

    // Override img to handle video embeds + image sizing + lightbox
    img({ src, alt, ...props }) {
      const imgSrc = typeof src === "string" ? src : "";
      if (!imgSrc) return <img src={imgSrc} alt={alt} loading="lazy" {...props} />;

      // YouTube embed
      const ytId = getYouTubeId(imgSrc);
      if (ytId) {
        return (
          <span className="video-embed">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${ytId}`}
              title={alt || "YouTube video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            {alt && <span className="video-caption">{alt}</span>}
          </span>
        );
      }

      // Vimeo embed
      const vimeoId = getVimeoId(imgSrc);
      if (vimeoId) {
        return (
          <span className="video-embed">
            <iframe
              src={`https://player.vimeo.com/video/${vimeoId}?dnt=1`}
              title={alt || "Vimeo video"}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
            {alt && <span className="video-caption">{alt}</span>}
          </span>
        );
      }

      // Direct video file
      if (isDirectVideo(imgSrc)) {
        return (
          <span className="video-embed video-embed-direct">
            <video controls preload="metadata">
              <source src={imgSrc} />
              Your browser does not support the video element.
            </video>
            {alt && <span className="video-caption">{alt}</span>}
          </span>
        );
      }

      // Regular image with optional dimension parsing + lightbox click + content overlay
      const { cleanAlt, width, height } = parseDimensions(alt);
      const sizeStyle: React.CSSProperties = {};
      if (width) sizeStyle.width = width;
      if (height) sizeStyle.height = height;
      if (width && !height) sizeStyle.height = "auto";

      const openLightbox = () => setLightboxRef.current({ src: imgSrc, label: cleanAlt });

      const handleImageCopy = async () => {
        try {
          const blob = await imageToPngBlob(imgSrc);
          await copyImageToClipboard(blob);
        } catch {
          // Cross-origin fallback: copy URL as text
          try {
            await copyTextToClipboard(imgSrc);
          } catch {
            // Both clipboard methods failed (e.g. non-secure context)
          }
        }
      };

      return (
        <ContentOverlay
          type="image"
          onCopy={handleImageCopy}
          onExpand={openLightbox}
        >
          <span
            className="md-image-wrapper"
            role="button"
            tabIndex={0}
            onClick={openLightbox}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openLightbox();
              }
            }}
          >
            <img
              src={imgSrc}
              alt={cleanAlt}
              loading="lazy"
              style={width || height ? sizeStyle : undefined}
              {...props}
            />
            {cleanAlt && <span className="md-image-caption">{cleanAlt}</span>}
          </span>
        </ContentOverlay>
      );
    },

    // Better table rendering — click to enlarge + content overlay copy/expand
    table: (props: React.ComponentPropsWithoutRef<'table'>) => (
      <TableExpandable
        {...props}
        onExpand={(html) => setLightboxRef.current({ htmlContent: html, label: "Table" })}
      />
    ),

    // Heading overrides — assign slugified IDs for scroll spy + getElementById
    h1: ({ children }) => <h1 id={slugify(getTextContent(children))}>{children}</h1>,
    h2: ({ children }) => <h2 id={slugify(getTextContent(children))}>{children}</h2>,
    h3: ({ children }) => <h3 id={slugify(getTextContent(children))}>{children}</h3>,
    h4: ({ children }) => <h4 id={slugify(getTextContent(children))}>{children}</h4>,
    h5: ({ children }) => <h5 id={slugify(getTextContent(children))}>{children}</h5>,
    h6: ({ children }) => <h6 id={slugify(getTextContent(children))}>{children}</h6>,
  }), []);

  const containerRef = useRef<HTMLDivElement>(null);

  // Detect overflowing KaTeX display formulas and add scroll affordance
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scrollHandlers = new Map<Element, EventListener>();

    const checkFormulas = () => {
      const formulas = container.querySelectorAll(".katex-display");
      formulas.forEach((el) => {
        const isOverflowing = el.scrollWidth > el.clientWidth + 2;
        el.classList.toggle("formula-overflowing", isOverflowing);

        if (isOverflowing && !scrollHandlers.has(el)) {
          const handler = () => {
            const atEnd =
              el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
            el.classList.toggle("formula-scrolled-end", atEnd);
          };
          el.addEventListener("scroll", handler, { passive: true });
          scrollHandlers.set(el, handler);
          // Initial check
          handler();
        } else if (!isOverflowing && scrollHandlers.has(el)) {
          el.removeEventListener("scroll", scrollHandlers.get(el)!);
          scrollHandlers.delete(el);
          el.classList.remove("formula-overflowing", "formula-scrolled-end");
        }
      });
    };

    // Check after render
    const raf = requestAnimationFrame(checkFormulas);

    // Re-check on resize
    const ro = new ResizeObserver(checkFormulas);
    ro.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      scrollHandlers.forEach((handler, el) => {
        el.removeEventListener("scroll", handler);
      });
    };
  }, [content]);

  return (
    <div ref={containerRef} className={`markdown-preview ${className || ""}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks, [remarkMath, { singleDollarTextMath: false }]]}
        rehypePlugins={[[rehypeKatex, { strict: "ignore" }], [rehypeHighlight, { plainText: ["mermaid"] }]]}
        components={components}
      >
        {content}
      </ReactMarkdown>
      {lightbox && (
        <ContentLightbox
          state={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
